import PropTypes from 'prop-types';
import React, { memo } from 'react';
import { List, Map } from 'immutable';
import moment from 'moment';
import classNames from 'classnames';

import { UserInput } from '../UserInput';
import { UserRoleList } from '../UserRoleList';
import { UserDashboardTitleList } from '../UserDashboardTitleList';
import { UserAutocomplete } from '../UserAutocomplete';
import { UserInactiveCheckbox } from '../UserInactiveCheckbox';
import { UserDateInput } from '../UserDateInput';
import { FirefliesApiKey } from '../FirefliesApiKey';
import config from '../../../config';
import style from './style.module.css';

const INPUT_DATE_FORMAT = 'MM/DD/YYYY';

/**
 * User's form component.
 *
 * @param props {Object}.
 * @param props.roles {Immutable.List} List of roles.
 * @param props.formMode {string} Form mode (view|edit).
 * @param props.formError {Immutable.Map} Errors of the form fields.
 * @param props.selectedUser {Immutable.Map} Selected user.
 * @param props.onFormChange {function} Handle form changing.
 * @param props.onUserSuggestionSelected {function} Handle user suggestion.
 */
const UserInfoComponent = ({
  suggestions,
  roles,
  dashboardUserTitles,
  formMode,
  formError,
  selectedUser,
  titleSuggestionMode,
  supervisorSuggestionMode,
  moduleSuggestionMode,
  onFormChange,
  onUserSuggestionSelected,
  onUserSuggestionClose,
  reports,
  onHireEndDateChange,
  onSuggestionInputTitleClick,
}) => {
  const inputProps = { selectedUser, formMode, formError };
  const userReport = selectedUser.get('reportIds', List()).toJS();
  const userBrowseAccesses = selectedUser.get('browseAccesses', List()).toJS();
  const isMailingBacklog = selectedUser.size ? selectedUser.get('isMailingBacklog') : false;
  const shareFirefliesMeetings = selectedUser.get('shareFirefliesMeetings', false);
  const autoCompleteFuncs = {
    suggestions,
    onSuggestionSelect: onUserSuggestionSelected,
    onSuggestionClose: onUserSuggestionClose,
  };
  const disabled = formMode === 'view';
  const reportElements = reports.map(report => (
    <label key={report.id} className={style.checkbox} htmlFor={`report${report.id}`}>
      <input
        checked={userReport.indexOf(report.id) > -1}
        disabled={disabled}
        id={`report${report.id}`}
        name="reports"
        type="checkbox"
        value={report.id}
      />
      {report.name}
    </label>
  ));

  const browseAccessElements = config.browse.get('accesses').map(item => {
    const access = item.toJS();

    return (
      <label key={access.key} className={style.checkbox} htmlFor={`access-${access.name}`}>
        <input
          checked={userBrowseAccesses.indexOf(access.key) > -1}
          disabled={disabled}
          id={`access-${access.name}`}
          name="accesses"
          type="checkbox"
          value={access.key}
        />
        {access.title}
      </label>
    );
  });

  const isNewUser = selectedUser.get('id', -1) === -1;
  const hireDateInputValue = isNewUser ? moment(new Date()) : selectedUser.get('hireDate', '') || '';
  const startDateInputValue = selectedUser.get('startDate', '') || '';
  const endDateInputValue = selectedUser.get('endDate', '');

  return (
    <form className={style.host} onChange={onFormChange}>
      <fieldset className={style.section}>
        <legend>Basic User Info</legend>
        <div className={style.fields}>
          <UserInput {...inputProps} autoComplete="off" label="User Name" name="userName" />
          <UserInput {...inputProps} label="First Name" name="firstName" />
          <UserInput {...inputProps} label="Last Name" name="lastName" />
          <UserInput {...inputProps} label="Email" name="email" />
          <UserAutocomplete
            {...inputProps}
            {...autoCompleteFuncs}
            id="titleSuggestion"
            label="Title"
            mode={titleSuggestionMode}
            name="title"
            onBlur={onUserSuggestionClose}
            onSuggestionInputClick={onSuggestionInputTitleClick}
          />
          <UserDashboardTitleList {...inputProps} dashboardUserTitles={dashboardUserTitles} />
          <UserRoleList {...inputProps} roles={roles} />
          <UserAutocomplete
            {...inputProps}
            {...autoCompleteFuncs}
            id="moduleSuggestion"
            label="Module"
            mode={moduleSuggestionMode}
            name="module"
            onBlur={onUserSuggestionClose}
          />
          <UserAutocomplete
            {...inputProps}
            {...autoCompleteFuncs}
            id="supervisorSuggestion"
            label="Supervisor"
            mode={supervisorSuggestionMode}
            name="supervisor"
          />
        </div>
      </fieldset>

      <div className={style.sectionsRow}>
        <fieldset className={style.section}>
          <legend>Contact Info</legend>
          <div className={style.stackFields}>
            <div className={style.phone}>
              <UserInput {...inputProps} label="Office Phone" name="officePhone" />
              <UserInput {...inputProps} label="Extension" name="officePhoneExt" />
            </div>
            <UserInput {...inputProps} label="Direct Dial" name="directDial" />
            <UserInput {...inputProps} label="Cell Phone" name="cellPhone" />
          </div>
        </fieldset>

        <fieldset className={style.section}>
          <legend>Account Security</legend>
          <div className={style.stackFields}>
            <UserInput {...inputProps} autoComplete="new-password" label="Password" name="password" type="password" />
            <UserInput
              {...inputProps}
              autoComplete="new-password"
              label="Re-Enter Password"
              name="rePassword"
              type="password"
            />
            <div className={style.inactive}>
              <UserInactiveCheckbox {...inputProps} />
            </div>
          </div>
        </fieldset>

        <fieldset className={style.section}>
          <legend>Employment Dates</legend>
          <div className={style.stackFields}>
            <UserDateInput
              {...inputProps}
              format={INPUT_DATE_FORMAT}
              label="Hire Date"
              name="hireDate"
              onChange={date => onHireEndDateChange(date, 'hireDate')}
              value={hireDateInputValue}
              disabled
            />
            <UserDateInput
              {...inputProps}
              disabled={disabled}
              format={INPUT_DATE_FORMAT}
              label="Start Date"
              name="startDate"
              onChange={date => onHireEndDateChange(date, 'startDate')}
              value={startDateInputValue}
            />
            <UserDateInput
              {...inputProps}
              disabled={disabled}
              format={INPUT_DATE_FORMAT}
              label="End Date"
              name="endDate"
              onChange={date => onHireEndDateChange(date, 'endDate')}
              value={endDateInputValue}
            />
          </div>
        </fieldset>

        <div className={style.accessColumn}>
          <fieldset className={style.group}>
            <legend>Reports</legend>
            {reportElements}
            <label className={style.checkbox} htmlFor="isMailingBacklog">
              <input
                checked={isMailingBacklog}
                disabled={disabled}
                id="isMailingBacklog"
                name="isMailingBacklog"
                type="checkbox"
              />
              Mailing Backlog
            </label>
          </fieldset>
          <fieldset className={classNames(style.group, style.browseAccess)}>
            <legend>Browse Access</legend>
            {browseAccessElements}
          </fieldset>
        </div>
      </div>

      <fieldset className={style.section}>
        <legend>Integrations</legend>
        <div className={style.integrationFields}>
          <FirefliesApiKey {...inputProps} compact />
          <UserInput {...inputProps} label="Dialpad User ID" name="dialpadUserId" />
        </div>
        <fieldset className={style.subgroup}>
          <legend>Meeting Privacy</legend>
          <label className={style.checkbox} htmlFor="shareFirefliesMeetingsOnlyMe">
            <input
              checked={!shareFirefliesMeetings}
              disabled={disabled}
              id="shareFirefliesMeetingsOnlyMe"
              name="shareFirefliesMeetings"
              type="radio"
              value="false"
            />
            Only Me
          </label>
          <label className={style.checkbox} htmlFor="shareFirefliesMeetingsShare">
            <input
              checked={shareFirefliesMeetings}
              disabled={disabled}
              id="shareFirefliesMeetingsShare"
              name="shareFirefliesMeetings"
              type="radio"
              value="true"
            />
            Share with Participants/Teammates
          </label>
        </fieldset>
      </fieldset>
    </form>
  );
};

UserInfoComponent.propTypes = {
  dashboardUserTitles: PropTypes.instanceOf(List).isRequired,
  formError: PropTypes.instanceOf(Map).isRequired,
  formMode: PropTypes.string.isRequired,
  moduleSuggestionMode: PropTypes.string.isRequired,
  onFormChange: PropTypes.func.isRequired,
  onUserSuggestionClose: PropTypes.func.isRequired,
  onUserSuggestionSelected: PropTypes.func.isRequired,
  roles: PropTypes.instanceOf(List).isRequired,
  selectedUser: PropTypes.instanceOf(Map).isRequired,
  suggestions: PropTypes.instanceOf(List).isRequired,
  supervisorSuggestionMode: PropTypes.string.isRequired,
  titleSuggestionMode: PropTypes.string.isRequired,
};

export const UserInfo = memo(UserInfoComponent);
